import { useState, useEffect, useCallback, useRef } from 'react';
import { db, realtime } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { createPayment } from '../services/paymentService';
import { getBandwidthSummary } from '../services/stockService';

export const useBandwidth = () => {
  const { user, profile } = useAuth();
  const [packages, setPackages] = useState([]);
  const [activePackages, setActivePackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalPackages: 0,
    activeCount: 0,
    pausedCount: 0,
    totalPurchased: 0,
    totalRemaining: 0,
    totalUsed: 0,
  });
  const hasFetched = useRef(false);

  const fetchPackages = useCallback(async () => {
    if (!user || !profile) return;

    try {
      if (!hasFetched.current) {
        setLoading(true);
      }
      const { data, error } = await db.bandwidthPackages.getAll(user.id);

      if (error) throw error;

      let packagesData = data || [];

      const seen = {};
      const deduped = [];
      for (const pkg of packagesData) {
        if (!seen[pkg.proxy_type]) {
          seen[pkg.proxy_type] = true;
          deduped.push(pkg);
        } else {
          db.bandwidthPackages.delete(pkg.id);
        }
      }
      packagesData = deduped;

      const hasBasic = !!profile?.stock_subuser_id_basic;
      const hasPremium = !!profile?.stock_subuser_id_premium;
      const hasMonthly = !!profile?.stock_subuser_id_monthly;
      if (hasBasic || hasPremium || hasMonthly) {
        const [basicResult, premiumResult, monthlyResult] = await Promise.allSettled([
          hasBasic ? getBandwidthSummary('basic') : Promise.resolve({ success: false }),
          hasPremium ? getBandwidthSummary('premium') : Promise.resolve({ success: false }),
          hasMonthly ? getBandwidthSummary('monthly') : Promise.resolve({ success: false }),
        ]);

        const nameMap = {
          residential_basic: 'Residential Basic',
          residential_premium: 'Residential Premium',
          monthly_residential: 'Monthly Residential',
        };

        const applyBtData = (result, proxyType) => {
          if (result.status !== 'fulfilled' || !result.value.success) return;
          const raw = result.value.data || {};
          const d = raw.data || raw;
          const purchased = parseFloat(d.total_bandwidth_gb);
          const used = parseFloat(d.total_used_gb);

          if (isNaN(purchased) || isNaN(used)) return;

          const remaining = Math.max(0, purchased - used);
          const localPkg = packagesData.find(p => p.proxy_type === proxyType);

          if (localPkg) {
            localPkg.total_gb = purchased;
            localPkg.remaining_gb = remaining;
          } else if (purchased > 0) {
            packagesData.push({
              id: `bt-${proxyType}`,
              user_id: user.id,
              proxy_type: proxyType,
              name: nameMap[proxyType] || proxyType,
              total_gb: purchased,
              remaining_gb: remaining,
              status: 'active',
              created_at: new Date().toISOString(),
            });
          }
        };

        applyBtData(basicResult, 'residential_basic');
        applyBtData(premiumResult, 'residential_premium');
        applyBtData(monthlyResult, 'monthly_residential');
      }

      setPackages(packagesData);

      const active = packagesData.filter(p => p.status === 'active');
      setActivePackages(active);

      const totalPurchased = packagesData.reduce((sum, p) => sum + parseFloat(p.total_gb), 0);
      const totalRemaining = packagesData.reduce((sum, p) => sum + parseFloat(p.remaining_gb), 0);

      setStats({
        totalPackages: packagesData.length,
        activeCount: active.length,
        pausedCount: packagesData.filter(p => p.status === 'paused').length,
        totalPurchased,
        totalRemaining,
        totalUsed: totalPurchased - totalRemaining,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  }, [user, profile]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const recalculateStats = (packagesData) => {
    const totalPurchased = packagesData.reduce((sum, p) => sum + parseFloat(p.total_gb), 0);
    const totalRemaining = packagesData.reduce((sum, p) => sum + parseFloat(p.remaining_gb), 0);
    const activeCount = packagesData.filter(p => p.status === 'active').length;
    const pausedCount = packagesData.filter(p => p.status === 'paused').length;

    setStats({
      totalPackages: packagesData.length,
      activeCount,
      pausedCount,
      totalPurchased,
      totalRemaining,
      totalUsed: totalPurchased - totalRemaining,
    });
  };

  useEffect(() => {
    if (!user) return;

    const channel = realtime.subscribeToBandwidth(user.id, (payload) => {
      if (payload.eventType === 'INSERT') {
        setPackages(prev => {
          const updated = [payload.new, ...prev];
          recalculateStats(updated);
          return updated;
        });
        if (payload.new.status === 'active') {
          setActivePackages(prev => [payload.new, ...prev]);
        }
      } else if (payload.eventType === 'UPDATE') {
        setPackages(prev => {
          const updated = prev.map(p => (p.id === payload.new.id ? payload.new : p));
          recalculateStats(updated);
          return updated;
        });
        setActivePackages(prev =>
          prev.map(p => (p.id === payload.new.id ? payload.new : p))
            .filter(p => p.status === 'active')
        );
      } else if (payload.eventType === 'DELETE') {
        setPackages(prev => {
          const updated = prev.filter(p => p.id !== payload.old.id);
          recalculateStats(updated);
          return updated;
        });
        setActivePackages(prev => prev.filter(p => p.id !== payload.old.id));
      }
    });

    return () => {
      realtime.unsubscribe(channel);
    };
  }, [user]);

  const purchasePackage = async (proxyType, gbAmount) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      const { price, pricePerGb, error: priceError } = await db.pricingTiers.calculatePrice(
        proxyType,
        gbAmount
      );

      if (priceError) throw new Error(priceError);

      const result = await createPayment({
        proxyType,
        gbAmount,
        priceUsd: price,
        pricePerGb,
      });

      if (!result.success) {
        return { success: false, error: result.error };
      }

      return {
        success: true,
        paymentUrl: result.paymentUrl,
        orderId: result.orderId,
      };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const pausePackage = async (packageId) => {
    setPackages(prev => {
      const updated = prev.map(p => p.id === packageId ? { ...p, status: 'paused' } : p);
      recalculateStats(updated);
      return updated;
    });
    setActivePackages(prev => prev.filter(p => p.id !== packageId));

    try {
      const { data, error } = await db.bandwidthPackages.pause(packageId);
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      await fetchPackages();
      return { success: false, error: err.message };
    }
  };

  const resumePackage = async (packageId) => {
    setPackages(prev => {
      const updated = prev.map(p => p.id === packageId ? { ...p, status: 'active' } : p);
      recalculateStats(updated);
      return updated;
    });

    try {
      const { data, error } = await db.bandwidthPackages.resume(packageId);
      if (error) throw error;
      if (data) {
        setActivePackages(prev => [...prev, data]);
      }
      return { success: true, data };
    } catch (err) {
      await fetchPackages();
      return { success: false, error: err.message };
    }
  };

  const getPackageByType = (proxyType) => {
    return activePackages.find(p => p.proxy_type === proxyType);
  };

  return {
    packages,
    activePackages,
    stats,
    loading,
    error,
    purchasePackage,
    pausePackage,
    resumePackage,
    getPackageByType,
    refresh: fetchPackages,
  };
};

export default useBandwidth;
